
import { useCallback } from "react";
import { addEdge, Node, Edge, Connection } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Account, CustomerContact, SerializedEdge } from "../types";

export const useOrgChartActions = (
  selectedAccount: Account | null,
  user: any,
  nodes: Node[],
  edges: Edge[],
  setEdges: (edges: Edge[]) => void,
  setContacts: (contacts: CustomerContact[]) => void,
  setNodes: (nodes: Node[]) => void,
  onNodesChange: (changes: any) => void,
  serializeEdges: (edges: Edge[]) => SerializedEdge[],
  loadContacts: () => Promise<CustomerContact[] | null>,
  loadRelationships: () => Promise<any[]>,
  updateFlowNodes: (contacts: CustomerContact[], relationships: any[]) => void,
  canEdit: boolean = false
) => {
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    console.log('Node clicked:', node);
  }, []);

  const handleNodesChange = useCallback(async (changes: any) => {
    if (!canEdit) return;
    
    onNodesChange(changes);
    
    // Save position changes to database
    const positionChanges = changes.filter((change: any) => 
      change.type === 'position' && change.dragging === false
    );
    
    if (positionChanges.length > 0 && selectedAccount?.company_id && user?.id) {
      for (const change of positionChanges) {
        const nodeId = change.id;
        const position = change.position;
        
        // First, check if a relationship record exists for this contact
        const { data: existingRelationship } = await supabase
          .from('org_chart_relationships')
          .select('*')
          .eq('from_contact_id', parseInt(nodeId))
          .eq('company_id', selectedAccount.company_id)
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (existingRelationship) {
          // Update existing relationship
          const { error } = await supabase
            .from('org_chart_relationships')
            .update({
              position_x: position.x,
              position_y: position.y
            })
            .eq('from_contact_id', parseInt(nodeId))
            .eq('company_id', selectedAccount.company_id)
            .eq('user_id', user.id);
            
          if (error) {
            console.error('Error updating position:', error);
          }
        } else {
          // Create new relationship record for position tracking
          const { error } = await supabase
            .from('org_chart_relationships')
            .insert({
              from_contact_id: parseInt(nodeId),
              company_id: selectedAccount.company_id,
              user_id: user.id,
              position_x: position.x,
              position_y: position.y,
              edge_data: []
            });
            
          if (error) {
            console.error('Error creating position record:', error);
          }
        }
      }
    }
  }, [canEdit, onNodesChange, selectedAccount, user]);

  const onConnect = useCallback(async (params: Connection) => {
    if (!canEdit) {
      toast.error("Only account owners can create connections");
      return;
    }
    
    const newEdge: Edge = {
      id: `e${params.source}-${params.target}`,
      source: params.source!,
      target: params.target!,
      type: 'default'
    };
    
    const updatedEdges = addEdge(newEdge, edges);
    setEdges(updatedEdges);
    
    // Save edge data to database
    if (selectedAccount?.company_id && user?.id) {
      const serializedEdges = serializeEdges(updatedEdges);
      
      // Check if relationship record exists for the source contact
      const { data: existingRelationship } = await supabase
        .from('org_chart_relationships')
        .select('*')
        .eq('from_contact_id', parseInt(params.source!))
        .eq('company_id', selectedAccount.company_id)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingRelationship) {
        // Update existing relationship with new edge data
        const { error } = await supabase
          .from('org_chart_relationships')
          .update({
            to_contact_id: parseInt(params.target!),
            edge_data: serializedEdges
          })
          .eq('from_contact_id', parseInt(params.source!))
          .eq('company_id', selectedAccount.company_id)
          .eq('user_id', user.id);
          
        if (error) {
          console.error('Error updating connection:', error);
          toast.error("Failed to save connection");
        }
      } else {
        // Create new relationship record
        const { error } = await supabase
          .from('org_chart_relationships')
          .insert({
            from_contact_id: parseInt(params.source!),
            to_contact_id: parseInt(params.target!),
            company_id: selectedAccount.company_id,
            user_id: user.id,
            edge_data: serializedEdges
          });
          
        if (error) {
          console.error('Error saving connection:', error);
          toast.error("Failed to save connection");
        }
      }
    }
  }, [canEdit, edges, setEdges, selectedAccount, user, serializeEdges]);

  const onEdgesDelete = useCallback(async (edgesToDelete: Edge[]) => {
    if (!canEdit) {
      toast.error("Only account owners can delete connections");
      return;
    }
    
    const updatedEdges = edges.filter(edge => 
      !edgesToDelete.some(deleteEdge => deleteEdge.id === edge.id)
    );
    setEdges(updatedEdges);
    
    // Update database with new edge data
    if (selectedAccount?.company_id && user?.id) {
      const serializedEdges = serializeEdges(updatedEdges);
      
      // Update all relationships for this company with new edge data
      const { error } = await supabase
        .from('org_chart_relationships')
        .update({ edge_data: serializedEdges })
        .eq('company_id', selectedAccount.company_id)
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error updating edges:', error);
        toast.error("Failed to update connections");
      }
    }
  }, [canEdit, edges, setEdges, selectedAccount, user, serializeEdges]);

  const handleSaveContact = async (editingContact: Partial<CustomerContact>) => {
    if (!canEdit) {
      toast.error("Only account owners can save contacts");
      return null;
    }

    if (!selectedAccount?.company_id || !user?.id) {
      toast.error("User not authenticated or no account selected");
      return null;
    }

    try {
      if (editingContact.id) {
        // Update existing contact
        const { error } = await supabase
          .from('customer_contacts')
          .update({
            customer_name: editingContact.customer_name,
            title: editingContact.title,
            support_status: editingContact.support_status,
            executive_mapped: editingContact.executive_mapped,
            status: editingContact.status,
            user_id: user.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingContact.id);

        if (error) {
          console.error('Error updating contact:', error);
          toast.error("Failed to update contact");
          return null;
        }
        
        toast.success("Contact updated successfully");
      } else {
        // Create new contact
        const { data, error } = await supabase
          .from('customer_contacts')
          .insert([{
            customer_name: editingContact.customer_name || '',
            title: editingContact.title || '',
            support_status: editingContact.support_status || 'Neutral',
            executive_mapped: editingContact.executive_mapped || '',
            status: editingContact.status || '',
            company_id: selectedAccount.company_id,
            user_id: user.id
          }])
          .select();

        if (error) {
          console.error('Error creating contact:', error);
          toast.error("Failed to create contact");
          return null;
        }
        
        toast.success("Contact created successfully");
        
        // Create initial position record for the new contact
        if (data && data.length > 0) {
          const newContactId = data[0].id;
          const { error: positionError } = await supabase
            .from('org_chart_relationships')
            .insert({
              from_contact_id: newContactId,
              company_id: selectedAccount.company_id,
              user_id: user.id,
              position_x: Math.random() * 500,
              position_y: Math.random() * 400,
              edge_data: []
            });
            
          if (positionError) {
            console.error('Error creating position record:', positionError);
          }
        }
      }

      // Reload data
      const [contactsData, relationshipsData] = await Promise.all([
        loadContacts(),
        loadRelationships()
      ]);
      
      if (contactsData && relationshipsData) {
        updateFlowNodes(contactsData, relationshipsData);
      }
      
      return editingContact.id ? null : [editingContact];
    } catch (error) {
      console.error('Error in handleSaveContact:', error);
      toast.error("An unexpected error occurred");
      return null;
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!canEdit) {
      toast.error("Only account owners can delete contacts");
      return;
    }

    try {
      // Delete from customer_contacts
      const { error: contactError } = await supabase
        .from('customer_contacts')
        .delete()
        .eq('id', id);

      if (contactError) {
        console.error('Error deleting contact:', contactError);
        toast.error("Failed to delete contact");
        return;
      }

      // Delete related org_chart_relationships records
      const { error: relationshipError } = await supabase
        .from('org_chart_relationships')
        .delete()
        .or(`from_contact_id.eq.${id},to_contact_id.eq.${id}`);

      if (relationshipError) {
        console.error('Error deleting relationships:', relationshipError);
      }

      toast.success("Contact deleted successfully");
      
      // Reload data
      const [contactsData, relationshipsData] = await Promise.all([
        loadContacts(),
        loadRelationships()
      ]);
      
      if (contactsData && relationshipsData) {
        updateFlowNodes(contactsData, relationshipsData);
      }
    } catch (error) {
      console.error('Error in handleDeleteContact:', error);
      toast.error("An unexpected error occurred");
    }
  };

  return {
    handleNodeClick,
    handleNodesChange,
    onConnect,
    onEdgesDelete,
    handleSaveContact,
    handleDeleteContact
  };
};
